/*
 * CART MILESTONES (customeclat) — v4
 *
 * Thresholds are defined in EUR (shop base currency). On non-EUR markets they are converted with
 * Shopify.currency.rate and rounded UP to a whole unit of the local currency — the Cart Milestones
 * app (Shopify Functions) applies the IDENTICAL formula server-side (cart.presentmentCurrencyRate).
 *
 * Loaded globally from theme.liquid (NOT from the snippet — the snippet does not render for an
 * empty cart, so a snippet-embedded script would never load on pages visited before the first
 * add-to-cart). Resilience against the theme's AJAX cart updates comes from three layers:
 *   1. MutationObserver — any DOM change that leaves an un-rendered [data-milestones] wrap
 *      (bundled-sections replaceChildren, refreshCart, morphing — any mechanism) schedules a render;
 *   2. "cart:change" — recomputes the qualifying subtotal client-side from the event payload
 *      (same approach as the theme's own FreeShippingBar) and re-renders immediately;
 *   3. every render pass is idempotent over ALL wraps, so double triggers are harmless.
 *
 * UX layer only — pricing is enforced server-side; nothing here is a security boundary.
 */
(function () {
  if (window.__eclatMilestonesInit) return;
  window.__eclatMilestonesInit = true;

  let removing = false;
  let scheduled = false;
  let staleMarkup = false; // renderInstance wykrył wiersz bez affordance'u — patrz run()
  let staleRefreshAt = 0; // cooldown wymuszonych odświeżeń sekcji (anty-pętla)
  let giftsExpanded = false; // accordion listy korzyści — przeżywa AJAX-owe re-rendery
  let openPicker = null; // level (string) aktualnie rozwiniętego inline pickera
  let qualifyingOverride = null; // latest client-side computation from cart:change, wins over server payload
  let claimedOverride = null; // Set of levels whose gift is in the cart, from the latest cart payload
  let lastUnclaimedGifts = 0; // unlocked-but-unclaimed gift levels, from the latest render pass
  let pendingCheckout = null; // the checkout button intercepted by the gift nudge
  let bypassNudge = false; // one-shot pass for the popup's own "continue to checkout" click
  let pendingWallet = false; // the nudge was opened by the wallet-buttons shield
  let walletBypass = false; // shields stay down after a conscious "continue" (re-armed on cart change)

  function updateWalletShields() {
    document.querySelectorAll('[data-ms-wallet-shield]').forEach((shield) => {
      shield.hidden = lastUnclaimedGifts === 0 || walletBypass;
    });
  }

  /* ------------------------------- Helpers --------------------------------- */

  function freshCart() {
    return fetch(`/cart.js?ts=${Date.now()}`, { cache: 'no-store' }).then((r) => r.json());
  }

  function notifyCartChanged() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (window.location.pathname.endsWith('/cart')) {
      window.location.reload();
    }
  }

  // Samples carry `_is_gift` (legacy free-samples marker), milestone gifts carry `_milestone_level`
  // ONLY — reusing `_is_gift` would make the theme's sample counter treat gifts as samples.
  function isGiftItem(item) {
    const props = item.properties || {};
    return props._is_gift === 'true' || Boolean(props._milestone_level);
  }

  function claimedLevelsFromCart(cart) {
    const levels = new Set();
    cart.items.forEach((item) => {
      const level = parseInt(item.properties && item.properties._milestone_level, 10);
      if (Number.isFinite(level)) levels.add(level);
    });
    return levels;
  }

  // Mirrors the Liquid computation in cart-milestones-config.liquid.
  function computeQualifyingFromCart(cart) {
    let total = cart.items.reduce((sum, item) => sum + (isGiftItem(item) ? 0 : item.final_line_price), 0);
    (cart.cart_level_discount_applications || []).forEach((discount) => {
      total -= discount.total_allocated_amount || 0;
    });
    return Math.max(0, total);
  }

  /*
   * The cart currency and locale come from the server-rendered config payload — window.Shopify
   * globals are NOT guaranteed on every preview domain (e.g. *.shopifypreview.com share links).
   * Shopify.currency.rate is needed ONLY when converting EUR thresholds to another currency.
   */
  function moneyFormatter(config) {
    const locale =
      config.locale || (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en';
    const currency =
      config.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active);
    if (!currency) return null;
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency });
    } catch (e) {
      return null;
    }
  }

  /*
   * Shared conversion contract (mirrored in the discount function — do not change unilaterally):
   * - entry currency equals the cart currency -> exact amount, no rounding;
   * - otherwise -> ceil(amount * rate / 100) * 100 (round UP to a whole major unit).
   * Returns null when the threshold cannot be computed safely (fail closed: UI stays hidden).
   */
  function resolveThreshold(level, cartCurrency) {
    if (!cartCurrency) return null;

    if (level.currency === cartCurrency) return level.amount;

    if (level.currency === 'EUR') {
      const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      if (!Number.isFinite(rate) || rate <= 0) return null;
      return Math.ceil((level.amount * rate) / 100) * 100;
    }

    return null;
  }

  /* ------------------------------- Rendering -------------------------------- */

  function renderInstance(wrap) {
    const configNode = wrap.querySelector('[data-milestones-config]');
    if (!configNode) return null;

    let config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (e) {
      return null;
    }

    const formatter = moneyFormatter(config);
    if (!formatter) return null;

    const cartCurrency =
      config.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active);
    const qualifying = qualifyingOverride !== null ? qualifyingOverride : config.qualifying;

    const levels = [];
    for (const lvl of config.levels) {
      let threshold = resolveThreshold(lvl, cartCurrency);

      // Shipping zone in a foreign fixed currency on a mismatched session (e.g. GB zone in GBP
      // while the cart is EUR — GB visitor on an EUR market, or preview-domain default context):
      // fall back to the level's DEFAULT zone amount (EUR) instead of hiding the milestone.
      // Display-only approximation — the actual shipping cost is enforced by shipping rates.
      if (threshold === null && lvl.kind === 'shipping' && typeof lvl.fallback === 'number' && lvl.fallback > 0) {
        threshold = resolveThreshold({ amount: lvl.fallback, currency: 'EUR' }, cartCurrency);
      }

      // Any other unresolvable level: SKIP it (per-level fail-closed), don't hide the whole UI.
      if (threshold === null) continue;
      levels.push({ ...lvl, threshold, reached: qualifying >= threshold });
    }
    if (levels.length === 0) return null; // nothing resolvable at all -> keep hidden

    levels.sort((a, b) => a.threshold - b.threshold);
    const max = levels.length ? levels[levels.length - 1].threshold : 0;
    if (max <= 0) return null;

    const next = levels.find((l) => !l.reached) || null;
    const money = (minor) => formatter.format(minor / 100);

    const messageEl = wrap.querySelector('[data-ms-message]');
    if (messageEl) {
      if (next) {
        const template = config.messages['to_level_' + next.level] || '';
        let text = template.replace('{amount}', money(next.threshold - qualifying));
        // Prefix = the HIGHEST currently unlocked reward ("Gratis Bodycream freigeschaltet — ...").
        // Levels are threshold-sorted, so the reached ones form a prefix of the array and the last
        // of them is the top unlocked. Full per-level phrases (not a "{reward} unlocked" template)
        // because adjective agreement differs per language.
        const topReached = levels.filter((l) => l.reached).pop();
        const unlockedPrefix = topReached && config.messages['unlocked_prefix_' + topReached.level];
        if (unlockedPrefix) {
          text = unlockedPrefix + ' — ' + text;
        }
        messageEl.textContent = text;
      } else {
        messageEl.textContent = config.messages.all_reached;
      }
    }

    const pct = Math.min(100, (qualifying / max) * 100);
    const fill = wrap.querySelector('[data-ms-fill]');
    if (fill) fill.style.width = pct + '%';
    const track = wrap.querySelector('[data-ms-track]');
    if (track) track.setAttribute('aria-valuenow', String(Math.round(pct)));

    // Hide leftover DOM (dot/row) of levels skipped as unresolvable — Liquid renders them all.
    const resolved = new Set(levels.map((l) => l.level));
    for (const lvl of config.levels) {
      if (resolved.has(lvl.level)) continue;
      const dot = wrap.querySelector(`[data-ms-dot="${lvl.level}"]`);
      if (dot) dot.style.display = 'none';
      const row = wrap.querySelector(`[data-ms-row="${lvl.level}"]`);
      if (row) row.style.display = 'none';
    }

    let unclaimedGifts = 0;

    for (const lvl of levels) {
      const dot = wrap.querySelector(`[data-ms-dot="${lvl.level}"]`);
      if (dot) {
        dot.style.display = '';
        dot.style.insetInlineStart = (lvl.threshold / max) * 100 + '%';
        dot.title = money(lvl.threshold);
        dot.classList.toggle('ms-dot--reached', lvl.reached);
      }

      // Shipping row has no "claim" step — reaching the threshold IS the reward (check shows).
      const claimed =
        lvl.kind === 'shipping'
          ? lvl.reached
          : claimedOverride !== null
            ? claimedOverride.has(lvl.level)
            : Boolean(lvl.hasGift);

      if (lvl.kind === 'gift' && lvl.reached && !claimed) unclaimedGifts++;

      const row = wrap.querySelector(`[data-ms-row="${lvl.level}"]`);
      if (row) {
        // NIEŚWIEŻY MARKUP: wiersz prezentu jest (już) nieodebrany, ale HTML pochodzi z renderu,
        // gdy prezent SIEDZIAŁ w koszyku — serwer nie dał mu wtedy affordance'u wyboru
        // (data-ms-open/-pickable ani modala/pickera). Klik nic by nie otworzył — trzeba
        // dociągnąć świeży render sekcji. [data-ms-plus] odsiewa wiersze celowo bierne
        // (wyprzedana kolekcja) — inaczej pętla odświeżeń. Repro: usuń prezent z koszyka.
        if (
          lvl.kind === 'gift' &&
          lvl.reached &&
          !claimed &&
          row.querySelector('[data-ms-plus]') &&
          !row.hasAttribute('data-ms-pickable') &&
          !row.hasAttribute('data-ms-open')
        ) {
          staleMarkup = true;
        }

        row.classList.toggle('ms-row--locked', !lvl.reached);
        row.classList.toggle('ms-row--claimed', claimed);
        // Stan dla accordionu (syncDisclosure czyta z DOM — działa też po AJAX-owej podmianie)
        row.dataset.msReached = lvl.reached ? '1' : '';
        row.dataset.msClaimed = claimed ? '1' : '';
        row.dataset.msThreshold = String(lvl.threshold);

        const unlockLabel = row.querySelector('[data-ms-unlock-label]');
        if (unlockLabel) {
          unlockLabel.textContent = config.messages.unlocks_at.replace('{amount}', money(lvl.threshold));
        }
      }
    }

    syncDisclosure(wrap, config);

    wrap.classList.add('ms-ready');
    return {
      reached: levels.filter((l) => l.reached).map((l) => l.level),
      // Prezenty "osierocone" przez podmianę kolekcji (produkt wypadł z primary i backupu):
      // funkcja nie da im rabatu, więc reconcile ma je usunąć z koszyka.
      invalidGiftLevels: levels
        .filter((l) => l.kind === 'gift' && l.hasGift && l.giftValid === false)
        .map((l) => l.level),
      unclaimedGifts,
    };
  }

  /*
   * Accordion listy korzyści + inline pickery. Czyta stan z DOM (datasety ustawia
   * renderInstance), więc można go wywołać samodzielnie po kliknięciach — bez fetchy.
   * Fokus (jedyny wiersz widoczny po zwinięciu), wg progów rosnąco:
   *   1) pierwszy odblokowany-nieodebrany prezent (jest akcja do wykonania),
   *   2) w przeciwnym razie pierwszy nieosiągnięty próg,
   *   3) a gdy wszystko zdobyte — najwyższa korzyść (z ✓).
   */
  function syncDisclosure(wrap, config) {
    const rows = Array.from(wrap.querySelectorAll('[data-ms-row]')).filter(
      (row) => row.style.display !== 'none',
    );
    rows.sort((a, b) => (parseFloat(a.dataset.msThreshold) || 0) - (parseFloat(b.dataset.msThreshold) || 0));

    const focusRow =
      rows.find((r) => r.dataset.msKind === 'gift' && r.dataset.msReached && !r.dataset.msClaimed) ||
      rows.find((r) => !r.dataset.msReached) ||
      rows[rows.length - 1] ||
      null;

    // Wiersz próbek: ZAWSZE widoczny (wymóg biznesowy), zawsze pierwszy.
    const staticRow = wrap.querySelector('[data-ms-static-row]');
    if (staticRow) {
      staticRow.classList.remove('ms-row--collapsed');
      staticRow.style.order = '0';
    }

    // Kolejność wizualna = progi rosnąco (flex order; na GB krem wskakuje przed dostawę).
    // Picker jedzie tuż za swoim wierszem (order + 1).
    let collapsibleCount = 0;
    rows.forEach((row, index) => {
      const order = (index + 1) * 10;
      row.style.order = String(order);
      const picker = wrap.querySelector(`[data-ms-picker="${row.dataset.msRow}"]`);
      if (picker) picker.style.order = String(order + 1);

      const collapsed = row !== focusRow;
      row.classList.toggle('ms-row--collapsed', collapsed);
      if (collapsed) collapsibleCount++;
    });

    // Picker podąża za swoim wierszem: zwinięty razem z nim, otwarty tylko dla openPicker.
    // Prezent odebrany w międzyczasie -> picker znika z DOM przy re-renderze; tu tylko klasy.
    wrap.querySelectorAll('[data-ms-picker]').forEach((picker) => {
      const level = picker.dataset.msPicker;
      const row = wrap.querySelector(`[data-ms-row="${level}"]`);
      const rowVisible = row && !row.classList.contains('ms-row--collapsed');
      const rowUnlocked = row && !row.classList.contains('ms-row--locked') && !row.classList.contains('ms-row--claimed');
      const open = openPicker === level && rowUnlocked && (rowVisible || giftsExpanded);
      picker.classList.toggle('ms-row--collapsed', row ? row.classList.contains('ms-row--collapsed') : true);
      picker.classList.toggle('open', open);
      if (row && row.hasAttribute('data-ms-pickable')) row.setAttribute('aria-expanded', String(open));
    });

    wrap.classList.toggle('ms-expanded', giftsExpanded);

    const toggle = wrap.querySelector('[data-ms-toggle]');
    if (toggle) {
      toggle.hidden = collapsibleCount === 0;
      toggle.setAttribute('aria-expanded', String(giftsExpanded));
      const label = toggle.querySelector('[data-ms-toggle-label]');
      if (label && config.messages.show_all && config.messages.hide_all) {
        label.textContent = giftsExpanded ? config.messages.hide_all : config.messages.show_all;
      }
    }
  }

  function syncDisclosureAll() {
    document.querySelectorAll('[data-milestones]').forEach((wrap) => {
      const configNode = wrap.querySelector('[data-milestones-config]');
      if (!configNode) return;
      try {
        syncDisclosure(wrap, JSON.parse(configNode.textContent));
      } catch (e) {
        /* zignoruj — wrap bez configu */
      }
    });
  }

  function renderAll() {
    let reached = null;
    let invalidGiftLevels = [];
    document.querySelectorAll('[data-milestones]').forEach((wrap) => {
      const result = renderInstance(wrap);
      if (result !== null) {
        reached = result.reached;
        invalidGiftLevels = result.invalidGiftLevels || [];
        lastUnclaimedGifts = result.unclaimedGifts;
      }
    });
    updateWalletShields();
    return reached === null ? null : { reached, invalidGiftLevels };
  }

  /* --------------------- Gift line reconciliation --------------------------- */
  /*
   * Keeps gift lines in their canonical shape — exactly ONE line per reached level, quantity 1:
   *   - level no longer reached           -> remove the line (auto-remove below threshold);
   *   - quantity > 1 or a duplicate line  -> move the excess to a clean property-less line.
   * The excess move matters: extra units on a `_milestone_level` line are paid at full price
   * but the whole line is EXCLUDED from the qualifying subtotal (theme and function alike), so
   * the customer would pay without the bar progressing. On a clean line the same units merge
   * with the regular product line and count. Quantity selectors are hidden on gift rows, so
   * this only fires for side channels (cart permalinks, discount line splits, older markup).
   */

  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
  }

  async function reconcileGifts(state) {
    if (removing || state === null) return;
    removing = true;
    const { reached, invalidGiftLevels } = state;

    try {
      const cart = await freshCart();
      let dirty = false;
      const keptLevels = new Set();

      for (const item of cart.items) {
        const level = parseInt(item.properties && item.properties._milestone_level, 10);
        if (!Number.isFinite(level) || level <= 1) continue;

        // Poziom nieosiągnięty LUB prezent osierocony (produkt wypadł z kolekcji poziomu —
        // funkcja nie przyzna rabatu, klient zapłaciłby pełną cenę) -> linia znika.
        if (!reached.includes(level) || invalidGiftLevels.includes(level)) {
          await changeLine(item.key, 0);
          dirty = true;
          continue;
        }

        let excess = 0;
        if (keptLevels.has(level)) {
          // Duplicate gift line for a level we already kept — dissolve it entirely.
          excess = item.quantity;
          await changeLine(item.key, 0);
        } else {
          keptLevels.add(level);
          if (item.quantity > 1) {
            excess = item.quantity - 1;
            await changeLine(item.key, 1);
          }
        }

        if (excess > 0) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: item.variant_id, quantity: excess }] })
          });
          dirty = true;
        }
      }

      if (dirty) notifyCartChanged();
    } catch (err) {
      console.error('[cart-milestones] gift reconciliation failed:', err);
    } finally {
      removing = false;
    }
  }

  /* ------------------------------- Scheduling ------------------------------- */

  function run() {
    scheduled = false;
    staleMarkup = false;
    const state = renderAll();

    // Wiersz prezentu wrócił do stanu "nieodebrany", ale markup nie ma czym otworzyć
    // pickera (render serwera z czasów, gdy prezent był w koszyku) — dociągnij świeży
    // render sekcji: cart:refresh refetchuje drawer (theme.js refreshCart_fn), a na
    // stronie /cart notifyCartChanged robi pełny reload. Cooldown 3 s chroni przed
    // pętlą, gdyby świeży render z jakiegoś powodu nie naprawił stanu.
    if (staleMarkup && Date.now() - staleRefreshAt > 3000) {
      staleRefreshAt = Date.now();
      notifyCartChanged();
      return; // po odświeżeniu przyjdzie nowy render — reconcile zrobi kolejny przebieg
    }

    reconcileGifts(state);
  }

  function scheduleRun() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(run);
  }

  // Layer 1: any DOM change that leaves an un-rendered wrap behind (any replacement mechanism).
  const observer = new MutationObserver(() => {
    if (document.querySelector('[data-milestones]:not(.ms-ready)')) {
      scheduleRun();
    }
  });

  function start() {
    observer.observe(document.body, { childList: true, subtree: true });
    scheduleRun();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Layer 2: instant recompute from the theme's cart events (FreeShippingBar approach) —
  // covers updates where the wrap stays in place, and pre-empts the DOM swap for replaced ones.
  document.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    if (cart && Array.isArray(cart.items)) {
      qualifyingOverride = computeQualifyingFromCart(cart);
      claimedOverride = claimedLevelsFromCart(cart);
    }
    walletBypass = false; // cart changed -> re-arm the wallet shields
    scheduleRun();
  });
  document.addEventListener('cart:refresh', () => {
    qualifyingOverride = null; // a fresh server render follows; trust its payload
    claimedOverride = null;
    walletBypass = false;
    scheduleRun();
  });
  // Layer 2b: powrót z bfcache (przycisk "wstecz" z checkoutu, gdzie strażnik mógł
  // usunąć prezent). Przeglądarka przywraca stronę z pamięci, a nasze override'y
  // liczone PRZED wyjściem wygrywałyby ze świeżym payloadem sekcji — pasek pokazywałby
  // stary stan. Drawer sam się refetchuje na pageshow (theme.js onPageShow_fn), ale
  // strona /cart nie ma takiego mechanizmu — tam pełny reload przy realnym rozjeździe.
  window.addEventListener('pageshow', async (event) => {
    if (!event.persisted) return;
    qualifyingOverride = null;
    claimedOverride = null;

    try {
      const cart = await freshCart();
      const freshQualifying = computeQualifyingFromCart(cart);
      const freshClaimed = claimedLevelsFromCart(cart);

      if (window.location.pathname.endsWith('/cart')) {
        const configNode = document.querySelector('[data-milestones] [data-milestones-config]');
        let rendered = null;
        try {
          rendered = configNode ? JSON.parse(configNode.textContent) : null;
        } catch (e) {
          /* uszkodzony payload — potraktuj jak rozjazd */
        }
        const renderedClaimed = rendered
          ? (rendered.levels || []).filter((l) => l.hasGift).map((l) => l.level).sort().join(',')
          : '';
        const stale =
          !rendered ||
          rendered.qualifying !== freshQualifying ||
          renderedClaimed !== Array.from(freshClaimed).sort().join(',');
        if (stale) {
          window.location.reload(); // line-itemy strony /cart i tak są przeterminowane
          return;
        }
      }

      qualifyingOverride = freshQualifying;
      claimedOverride = freshClaimed;
    } catch (e) {
      /* zostaw override'y wyzerowane — payload sekcji przejmie prowadzenie */
    }
    scheduleRun();
  });
  document.addEventListener('cart-drawer:refreshed', scheduleRun);
  // Theme editor (Customize) re-injects sections with its own runtime — re-render there too.
  document.addEventListener('shopify:section:load', scheduleRun);

  /* ----------------------- Checkout nudge (popup) --------------------------- */
  // Capture phase: beats the form submit. Blocks EVERY checkout attempt while an unlocked gift
  // is unclaimed; only the popup's own "continue to checkout" button passes (one-shot bypass).
  document.addEventListener(
    'click',
    (event) => {
      const checkoutBtn = event.target.closest('button[name="checkout"]');
      if (!checkoutBtn) return;
      if (lastUnclaimedGifts === 0) return;
      if (bypassNudge) {
        bypassNudge = false;
        return;
      }

      // Pick the popup matching the clicked button's context: the drawer's own instance when the
      // click came from inside the drawer, otherwise the page instance. A blind querySelector
      // would grab the (hidden) drawer copy on /cart and "open" an invisible popup.
      const inDrawer = checkoutBtn.closest('cart-drawer');
      const popup =
        document.getElementById(inDrawer ? 'ms-checkout-popup-drawer' : 'ms-checkout-popup-page') ||
        document.querySelector('[data-ms-checkout-popup]');
      if (!popup) return;

      event.preventDefault();
      event.stopPropagation();
      pendingCheckout = checkoutBtn;
      popup.classList.add('open');
      document.body.classList.add('modal-open');
    },
    true,
  );

  /* ------------------------------ Gift picker ------------------------------ */

  document.addEventListener('click', async (event) => {
    const walletShield = event.target.closest('[data-ms-wallet-shield]');
    if (walletShield) {
      const popup =
        document.getElementById('ms-checkout-popup-page') ||
        document.querySelector('[data-ms-checkout-popup]');
      if (popup) {
        pendingCheckout = null;
        pendingWallet = true;
        popup.classList.add('open');
        document.body.classList.add('modal-open');
      }
      return;
    }

    const popupChoose = event.target.closest('[data-ms-popup-choose]');
    if (popupChoose) {
      const modal = popupChoose.closest('[data-ms-modal]');
      modal.classList.remove('open');
      document.body.classList.remove('modal-open');
      pendingCheckout = null;
      pendingWallet = false;
      const gifts = document.querySelector('.ms-gifts');
      if (gifts) {
        // Od razu rozwiń inline picker fokusowego prezentu — klient ląduje prosto w wyborze.
        const pickableRow = gifts.querySelector('[data-ms-pickable]:not(.ms-row--locked):not(.ms-row--claimed)');
        if (pickableRow) openPicker = pickableRow.getAttribute('data-ms-pickable');
        syncDisclosureAll();
        gifts.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const popupContinue = event.target.closest('[data-ms-popup-continue]');
    if (popupContinue) {
      const modal = popupContinue.closest('[data-ms-modal]');
      modal.classList.remove('open');
      document.body.classList.remove('modal-open');

      if (pendingWallet) {
        // Wallet flow: we cannot re-click inside a third-party iframe — drop the shields so the
        // customer's next tap reaches the wallet button directly.
        pendingWallet = false;
        walletBypass = true;
        updateWalletShields();
        return;
      }

      const btn = pendingCheckout;
      pendingCheckout = null;
      if (btn) {
        bypassNudge = true; // one-shot: the capture listener lets exactly this click through
        btn.click();
      }
      return;
    }

    // Accordion listy: pokaż/ukryj pozostałe korzyści.
    const toggleBtn = event.target.closest('[data-ms-toggle]');
    if (toggleBtn) {
      giftsExpanded = !giftsExpanded;
      syncDisclosureAll();
      return;
    }

    // Wiersz prezentu: rozwiń/zwiń inline picker (wersja accordion snippetu).
    const pickableRow = event.target.closest('[data-ms-pickable]');
    if (pickableRow) {
      if (pickableRow.classList.contains('ms-row--locked') || pickableRow.classList.contains('ms-row--claimed')) return;
      const level = pickableRow.getAttribute('data-ms-pickable');
      openPicker = openPicker === level ? null : level;
      if (openPicker) {
        const picker = document.getElementById(pickableRow.getAttribute('aria-controls'));
        const errEl = picker && picker.querySelector('[data-ms-error]');
        if (errEl) errEl.hidden = true; // stale failure note from a previous attempt
      }
      syncDisclosureAll();
      return;
    }

    // KOMPATYBILNOŚĆ WSTECZNA: stara (modalowa) wersja snippetu — wiersz z data-ms-open
    // otwiera modal pickera. Dzięki temu ten sam plik JS działa z OBIEMA wersjami snippetu
    // (bez okna niespójności przy wgrywaniu plików w dowolnej kolejności).
    const openBtn = event.target.closest('[data-ms-open]');
    if (openBtn) {
      const row = openBtn.closest('[data-ms-row]') || openBtn;
      if (row.classList.contains('ms-row--locked') || row.classList.contains('ms-row--claimed')) return;
      const modal = document.getElementById(openBtn.getAttribute('data-ms-open'));
      if (modal) {
        const errEl = modal.querySelector('[data-ms-error]');
        if (errEl) errEl.hidden = true;
        modal.classList.add('open');
        document.body.classList.add('modal-open');
      }
      return;
    }

    // Modal (już tylko checkout-popup): zamykanie krzyżykiem/overlayem.
    const closeBtn = event.target.closest('[data-ms-close]');
    const overlay = event.target.matches('[data-ms-modal]') ? event.target : null;
    if (closeBtn || overlay) {
      const modal = closeBtn ? closeBtn.closest('[data-ms-modal]') : overlay;
      modal.classList.remove('open');
      document.body.classList.remove('modal-open');
      return;
    }

    const pick = event.target.closest('[data-ms-pick]');
    if (pick) {
      // Kontener: inline picker (accordion) LUB modal (stara wersja snippetu)
      const picker = pick.closest('[data-ms-picker], [data-ms-modal]');
      if (!picker) return;
      picker.querySelectorAll('[data-ms-pick]').forEach((el) => el.setAttribute('aria-pressed', 'false'));
      pick.setAttribute('aria-pressed', 'true');
      const confirm = picker.querySelector('[data-ms-add]');
      if (confirm) confirm.disabled = false;
      return;
    }

    const addBtn = event.target.closest('[data-ms-add]');
    if (addBtn && !addBtn.disabled) {
      const picker = addBtn.closest('[data-ms-picker], [data-ms-modal]');
      if (!picker) return;
      const selected = picker.querySelector('[data-ms-pick][aria-pressed="true"]');
      if (!selected) return;

      addBtn.disabled = true;

      try {
        // `_ms_threshold` = próg poziomu w jednostkach mniejszych waluty koszyka —
        // czyta go rozszerzenie checkoutu (usuwanie prezentu, gdy kod rabatowy zepchnie
        // wartość zamówienia pod próg). To tylko strażnik polityki, nie granica
        // bezpieczeństwa: darmowość i tak przyznaje server-side cart transform.
        const level = addBtn.getAttribute('data-level');
        const wrap = addBtn.closest('[data-milestones]');
        const levelRow = wrap && wrap.querySelector(`[data-ms-row="${level}"]`);
        const properties = { _milestone_level: level };
        if (levelRow && levelRow.dataset.msThreshold) {
          properties._ms_threshold = levelRow.dataset.msThreshold;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: parseInt(selected.getAttribute('data-variant-id'), 10),
              quantity: 1,
              properties
            }]
          })
        });

        if (!response.ok) throw new Error(await response.text());

        openPicker = null; // prezent odebrany — picker znika przy re-renderze
        if (picker.matches('[data-ms-modal]')) {
          picker.classList.remove('open'); // stara wersja: domknij modal
          document.body.classList.remove('modal-open');
        }
        notifyCartChanged();
      } catch (err) {
        // Sold out between render and click, or a cart API hiccup — tell the customer
        // instead of failing silently; the picker stays open for another pick.
        console.error('[cart-milestones] add failed:', err);
        addBtn.disabled = false;
        const errEl = picker.querySelector('[data-ms-error]');
        if (errEl) errEl.hidden = false;
      }
    }
  });

  // A11y: wiersze prezentów mają role="button" — Enter/Spacja przełącza picker.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const row = event.target.closest && event.target.closest('[data-ms-pickable]');
    if (!row) return;
    event.preventDefault();
    row.click();
  });
})();
